import {
  Pagination,
  type Selection,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import type React from 'react';
import { useCallback, useState } from 'react';
import { FiExternalLink, FiInbox } from 'react-icons/fi';
import { LuLayoutGrid, LuLayoutList, LuRefreshCw } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { Heading } from '@/core/molecules';
import { Button, Chip, cn, Spinner, toaster } from '@/core/ui';
import { CreateWeeklyRecordReviewsDocument, type FilteredRecordReview } from '@/graphql/gql/graphql';
import { useMutation } from '@/libs';
import { useGlobal } from '@/store';
import { getDbTypeHashFragment } from '@/utils';
import { ReasonCell } from '../../components/reason-cell';
import { FeedbackEditModal } from '../../question/components/feedback-edit-modal';
import { FilteredRecordReviewsGrid } from './filtered-record-reviews-grid';
import type { WeeklyRecordsFilters } from './generation-form';

const LIMIT = 50;

const COLUMNS: Array<{ name: string; uid: string; sortable: boolean }> = [
  { name: 'SR NO.', uid: 'srNo', sortable: false },
  { name: 'ID', uid: 'id', sortable: false },
  { name: 'REASON', uid: 'reason', sortable: false },
  { name: 'LIKES/DISLIKES', uid: 'likes', sortable: false },
  { name: 'COMMENTS', uid: 'totalComments', sortable: false },
  { name: 'ATTEMPT %', uid: 'percentage', sortable: false },
  { name: 'Q-ID', uid: 'questionId', sortable: false },
];

const renderCell = (
  row: FilteredRecordReview,
  columnKey: string,
  dbType: ReturnType<typeof useGlobal.getState>['dbType'],
  serialNo: number,
) => {
  const questionId = row.question?.id ?? row.recordId;
  const editQuestionPath =
    questionId != null
      ? ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(questionId)) +
        getDbTypeHashFragment('global-state', { dbType })
      : null;
  const cellValue = columnKey in row ? row[columnKey as keyof FilteredRecordReview] : '-';

  switch (columnKey) {
    case 'srNo':
      return <span className='text-sm font-medium text-default-600'>{serialNo}</span>;

    case 'id':
      return <span className='text-sm font-mono text-default-600'>{row.recordId ?? '—'}</span>;
    case 'reason':
      return <ReasonCell reviewId={row.recordId?.toString() ?? ''} reasonText={row.reason ?? ''} />;
    case 'likes':
      return (
        <div className='flex items-center gap-3'>
          {row.likes !== null && row.likes !== undefined && (
            <span className='text-sm text-success-500'>👍 {row.likes}</span>
          )}
          {row.dislikes !== null && row.dislikes !== undefined && (
            <span className='text-sm text-danger-500'>👎 {row.dislikes}</span>
          )}
        </div>
      );
    case 'totalComments':
      return (
        <Chip
          size='sm'
          variant='faded'
          radius='sm'
          className='text-sm text-default-600'
          color='default'
          title={`Total comments${row.newComments ? ` (${row.newComments} new)` : ''}`}
        >
          {row.newComments || 0} / {typeof row.totalComments === 'number' ? row.totalComments : '—'}
        </Chip>
      );
    case 'questionId':
      return editQuestionPath ? (
        <Link
          target='_blank'
          rel='noopener noreferrer'
          to={editQuestionPath}
          className='flex items-center gap-2 text-sm text-default-600 hover:text-default-800 hover:underline'
        >
          <span className='font-mono'>{String(questionId)}</span>
          <FiExternalLink className='size-3.5' />
        </Link>
      ) : (
        <span className='text-sm text-default-400'>—</span>
      );
    default:
      return (
        <Chip radius='sm' size='sm' className='text-sm text-default-600' variant='faded' color='default'>
          {cellValue as string}
        </Chip>
      );
  }
};

type FilteredRecordReviewsTableProps = {
  title: string;
  filters: WeeklyRecordsFilters | null;
  skipQuery: boolean;
  isLoading: boolean;
  rows: FilteredRecordReview[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  onRefetch: () => void;
};

const getRowKey = (item: FilteredRecordReview) =>
  item.recordId != null ? String(item.recordId) : `row-${item.question?.id ?? Math.random()}`;

export const FilteredRecordReviewsTable: React.FC<FilteredRecordReviewsTableProps> = ({
  title,
  filters,
  skipQuery,
  isLoading,
  rows,
  total,
  page,
  onPageChange,
  onRefetch,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  const dbType = useGlobal.use.dbType();
  const rowsWithSerial = rows.map((row, index) => ({
    row,
    serialNo: (page - 1) * LIMIT + index + 1,
  }));

  const [createWeeklyRecordReviewsMutation, { loading: saveLoading }] = useMutation(
    CreateWeeklyRecordReviewsDocument,
  );

  const hasNoSelection = selectedKeys !== 'all' && (selectedKeys as Set<string>).size === 0;

  const handleSelectionChange = useCallback((keys: Selection) => {
    setSelectedKeys(keys);
  }, []);

  const handleSave = useCallback(async () => {
    const emptySelection = selectedKeys === 'all' ? false : (selectedKeys as Set<string>).size === 0;
    if (emptySelection) {
      toaster.error('Select at least one record or use the header to select all');
      return;
    }

    const isAllSelected = selectedKeys === 'all';

    try {
      if (isAllSelected) {
        if (!filters?.startDate || !filters?.endDate) {
          toaster.error('Start date and end date are required when saving all');
          return;
        }
        await createWeeklyRecordReviewsMutation({
          variables: {
            input: {
              startDate: filters.startDate,
              endDate: filters.endDate,
              dislikesThreshold: filters.dislikesThreshold ?? undefined,
              percentageLessThan: filters.percentageLessThan ?? undefined,
              percentageGreaterThan: filters.percentageGreaterThan ?? undefined,
            },
          },
        });
        toaster.success('Weekly reviews created for all filtered records');
      } else {
        const keys = selectedKeys as Set<string>;
        const questionIds = Array.from(keys)
          .map((k) => Number(k))
          .filter((id) => Number.isFinite(id));
        if (questionIds.length === 0) {
          toaster.error('No valid selection');
          return;
        }
        await createWeeklyRecordReviewsMutation({
          variables: {
            input: {
              questionIds,
              startDate: filters?.startDate,
              endDate: filters?.endDate,
              dislikesThreshold: filters?.dislikesThreshold ?? undefined,
              percentageLessThan: filters?.percentageLessThan ?? undefined,
              percentageGreaterThan: filters?.percentageGreaterThan ?? undefined,
            },
          },
        });
        toaster.success(`Weekly reviews created`);
      }
      setSelectedKeys(new Set());
      onRefetch();
    } catch (err) {
      toaster.error(err instanceof Error ? err.message : 'Failed to create weekly reviews');
    }
  }, [createWeeklyRecordReviewsMutation, filters, onRefetch, selectedKeys]);

  return (
    <div className='bg-background rounded-lg shadow-md flex flex-col h-full'>
      <Heading
        description={
          <span className='text-sm font-medium text-warning-600'>
            Showing {rows.length} records on page {page} of {Math.max(1, Math.ceil(total / LIMIT))} (Total:{' '}
            {total})
          </span>
        }
        textClassName='md:text-xl'
        title={title}
        className='md:px-6 py-2'
      >
        <div className='flex items-center gap-2'>
          <Button
            variant='bordered'
            size='sm'
            onPress={handleSave}
            isDisabled={saveLoading || skipQuery || hasNoSelection}
            isLoading={saveLoading}
          >
            Save
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('grid')}
            aria-label='Grid view'
          >
            <LuLayoutGrid className='size-4' />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('table')}
            aria-label='Table view'
          >
            <LuLayoutList className='size-4' />
          </Button>
          <Button
            variant='bordered'
            isIconOnly
            size='sm'
            onPress={onRefetch}
            isDisabled={isLoading}
            aria-label='Refetch'
          >
            <LuRefreshCw className={cn('size-4 text-default-600', { 'animate-spin': isLoading })} />
          </Button>
        </div>
      </Heading>

      {viewMode === 'grid' ? (
        isLoading ? (
          <div className='flex items-center justify-center min-h-[400px]'>
            <Spinner size='lg' />
          </div>
        ) : rowsWithSerial.length === 0 ? (
          <div className='flex flex-col space-y-4 items-center justify-center min-h-[400px]'>
            <FiInbox className='size-16 text-default-400' />
            <p className='text-default-400 text-2xl font-medium'>
              {skipQuery ? 'Set filters and click Generate' : 'No filtered records found'}
            </p>
          </div>
        ) : (
          <FilteredRecordReviewsGrid
            rows={rowsWithSerial}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
          />
        )
      ) : (
        <Table
          isHeaderSticky
          color='default'
          classNames={{
            base: '',
            wrapper: 'px-6 scrollbar-thin max-h-[calc(100vh-460px)] overflow-auto',
            tr: 'hover:bg-default-100',
            th: [''],
          }}
          isCompact
          shadow='none'
          aria-label='Filtered record reviews table'
          selectionMode='multiple'
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
        >
          <TableHeader columns={COLUMNS}>
            {(column) => (
              <TableColumn className='text-sm' key={column.uid}>
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={
              <div className='col-span-full flex flex-col space-y-4 items-center justify-center h-full'>
                <FiInbox className='size-16 text-default-400' />
                <p className='text-default-400 text-2xl font-medium'>
                  {skipQuery ? 'Set filters and click Generate' : 'No filtered records found'}
                </p>
              </div>
            }
            isLoading={isLoading}
            loadingContent={<Spinner size='lg' />}
            items={rowsWithSerial}
          >
            {(item) => (
              <TableRow key={getRowKey(item.row)}>
                {(columnKey) => (
                  <TableCell>{renderCell(item.row, columnKey as string, dbType, item.serialNo)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {total > 0 && (
        <div className='p-4 flex justify-between items-center border-t border-default-200'>
          <span className='w-[30%] text-small text-default-400'>
            Page {page} of {Math.max(1, Math.ceil(total / LIMIT))}
          </span>
          <Pagination
            isCompact
            showControls
            showShadow
            color='primary'
            page={page}
            total={Math.max(1, Math.ceil(total / LIMIT))}
            onChange={onPageChange}
          />
        </div>
      )}

      <FeedbackEditModal onSave={async () => {}} isLoading={false} />
    </div>
  );
};
